import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, ShoppingBag, GraduationCap, UserRound, Users, IndianRupee, ArrowRight, Check, ShieldCheck } from "lucide-react";
import api, { fmtINR } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { MKT_CSS, MktHeader } from "./MktShared";

const PERKS = [
  { icon: Briefcase, t: "Sell Services", d: "Offer fixed-price packages — logos, websites, marketing and more" },
  { icon: ShoppingBag, t: "Sell Digital Products", d: "Templates, tools and assets with instant delivery" },
  { icon: GraduationCap, t: "Sell Courses", d: "Teach what you know with structured modules & lessons" },
  { icon: UserRound, t: "Build Your Profile", d: "Portfolio, skills, ratings and verified reviews" },
  { icon: Users, t: "Reach Business Customers", d: "Thousands of MILTROO businesses already on the platform" },
  { icon: IndianRupee, t: "Earn Through MILTROO", d: "Secure payments, wallet, withdrawals to bank / UPI" },
];

export default function FreelancerLanding() {
  const { user } = useAuth();
  const [price, setPrice] = useState(99);
  useEffect(() => { api.get("/mkt/config").then((r) => setPrice(r.data.subscription_price)).catch(() => {}); }, []);

  const joinUrl = "/freelancer/apply";

  return (
    <div className="min-h-screen bg-background">
      <style>{MKT_CSS}</style>
      <MktHeader />

      <section className="mkt-hero relative overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center text-white">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 mkt-glass rounded-full px-4 py-1.5 text-xs font-bold tracking-wide uppercase">
            Freelancer Program
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.05]"
            data-testid="fl-landing-title">
            Sell Your Skills on MILTROO
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
            className="text-white/85 text-base sm:text-lg mt-4 max-w-2xl mx-auto">
            Apply with basic details, pay the membership, get verified by our team — then set up your full profile and start earning.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to={joinUrl} data-testid="fl-join-cta"
              className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold text-sm px-8 py-4 rounded-full hover:scale-105 active:scale-95 transition-transform shadow-xl">
              Join as Freelancer <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/marketplace" data-testid="fl-browse-cta"
              className="inline-flex items-center mkt-glass text-white font-bold text-sm px-8 py-4 rounded-full hover:bg-white/20 transition-colors">
              Browse Marketplace
            </Link>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 space-y-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PERKS.map((p, i) => (
            <motion.div key={p.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="mkt-card p-6">
              <div className="h-11 w-11 rounded-xl mkt-grad-fallback flex items-center justify-center mb-4"><p.icon className="h-5 w-5 text-white" /></div>
              <p className="font-display font-bold">{p.t}</p>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{p.d}</p>
            </motion.div>
          ))}
        </div>

        {/* MEMBERSHIP CARD */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="max-w-xl mx-auto rounded-3xl p-[1.5px] mkt-shine" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB,#7C3AED)" }}
          data-testid="fl-membership-card">
          <div className="rounded-3xl bg-card p-8 text-center space-y-5">
            <p className="overline-label">Freelancer Membership</p>
            <p className="font-display font-extrabold text-5xl tracking-tight" data-testid="fl-membership-price">
              {fmtINR(price)}<span className="text-lg text-muted-foreground font-bold"> / month</span>
            </p>
            <ul className="text-sm text-left space-y-2.5 max-w-sm mx-auto">
              {["Unlimited services, products & courses", "Secure chat + custom offers with buyers",
                "Wallet with bank / UPI withdrawals", "Ratings, reviews & featured placement eligibility"].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{t}</li>
              ))}
            </ul>
            <p className="text-[11px] text-muted-foreground flex items-start gap-1.5 justify-center">
              <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" />
              Your freelancer account is activated after payment verification and approval.
            </p>
            <Link to={joinUrl} data-testid="fl-join-cta-2"
              className="inline-flex items-center gap-2 gradient-bg text-white font-bold text-sm px-8 py-3.5 rounded-full glow-sm hover:scale-105 active:scale-95 transition-transform">
              Join as Freelancer <ArrowRight className="h-4 w-4" />
            </Link>
            {user?.role === "PARTNER" && (
              <p className="text-xs text-muted-foreground">You're already a MILTROO Partner — the same account will be used. No duplicate account needed.</p>
            )}
          </div>
        </motion.div>

        {/* HOW IT WORKS */}
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight mkt-sec-title text-center mb-8">How it works</h2>
          <div className="space-y-3">
            {["Create your free account & give basic details", `Pay ${fmtINR(price)} membership & submit payment reference`,
              "MILTROO admin verifies payment & approves manually", "Then complete your profile — skills, experience, portfolio, payout/bank details",
              "Publish services, chat with buyers, earn"].map((s, i) => (
              <motion.div key={s} initial={{ opacity: 0, x: -14 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="mkt-card !rounded-2xl p-4 flex items-center gap-4">
                <span className="h-9 w-9 rounded-full gradient-bg text-white font-display font-extrabold flex items-center justify-center shrink-0">{i + 1}</span>
                <p className="font-semibold text-sm">{s}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
